import moment from 'moment-timezone';

import {
    getEventDuration,
    getMaxEventDuration,
    shouldCheckForEventDuration,
} from '../../../../utils/events';
import {
    validateRecurringScheduleInstances,
    RECURRENCE_CADENCE,
    RECURRENCE_MAX_INSTANCES,
} from '../recurrenceUtils';

/**
 * Validates the schedule NestedField value.
 *
 * @param {Object} schedule - Field value (eventStartUtc, eventEndUtc, …)
 * @param {Object} ctx
 * @param {Object} ctx.values          - Full form values
 * @param {boolean} ctx.isRecurring
 * @param {string}  ctx.recurrenceCadenceKey
 * @param {boolean} ctx.isScheduleSingleDay
 * @param {Object}  ctx.clubSettings
 * @param {Object}  ctx.recurrenceSettings
 * @param {Array}   ctx.recurrenceInstances
 * @returns {Object|null}
 */
export function scheduleValidate(schedule, {
    values,
    isRecurring,
    recurrenceCadenceKey,
    isScheduleSingleDay,
    clubSettings,
    recurrenceSettings,
    recurrenceInstances,
}) {
    let anyErrors = false;
    const error = { schedule: {} };

    const eventStart = moment.utc(schedule.eventStartUtc);
    const eventEnd = moment.utc(schedule.eventEndUtc);
    const regStart = moment.utc(schedule.registrationStartUtc);
    const regEnd = moment.utc(schedule.registrationEndUtc);
    const drawDate = moment.utc(schedule.drawPublishedUtc);
    const maxEnd = moment.utc(schedule.maxEventEndUtc);

    if (schedule.eventEndUtc && schedule.registrationEndUtc && eventEnd.isSameOrBefore(regEnd)) {
        error.schedule.regEnd = 'Registration end date must be before the event end date.';
        anyErrors = true;
    }
    if (schedule.eventStartUtc && schedule.registrationStartUtc && eventStart.isSameOrBefore(regStart)) {
        error.schedule.regStart = 'Registration start date must be before the event begins date.';
        anyErrors = true;
    }
    if (schedule.eventStartUtc && schedule.drawPublishedUtc && eventStart.isSameOrBefore(drawDate)) {
        error.schedule.drawPost = 'Draw posting date must be before the event begins date.';
        anyErrors = true;
    }
    if (schedule.eventStartUtc && schedule.eventEndUtc && eventStart.isSameOrAfter(eventEnd)) {
        error.schedule.eventEnd = 'The event end date must be after the event begins date.';
        anyErrors = true;
    }
    if (schedule.registrationStartUtc && schedule.registrationEndUtc && regStart.isSameOrAfter(regEnd)) {
        error.schedule.regEnd = 'The registration end date must be after the registration begins date.';
        anyErrors = true;
    }
    if (schedule.maxEventEndUtc && schedule.eventEndUtc && eventEnd.isAfter(maxEnd)) {
        error.schedule.eventEnd = 'The event end date can not be more than 7 days apart from initial end date.';
        anyErrors = true;
    }
    if (shouldCheckForEventDuration(values, schedule)) {
        const maxDuration = getMaxEventDuration(clubSettings);
        if (getEventDuration(schedule) >= maxDuration) {
            error.schedule.eventEnd = `The event end date can not be more than ${maxDuration} days apart from event start date`;
            anyErrors = true;
        }
    }

    if (isRecurring) {
        if (recurrenceCadenceKey === RECURRENCE_CADENCE.DAILY && !isScheduleSingleDay) {
            error.recurrence = 'Daily recurrence is only available for single-day events.';
            anyErrors = true;
        }
        if ((!recurrenceSettings?.numberOfOccurrences || recurrenceSettings.numberOfOccurrences < 1) && !recurrenceSettings?.endsOnDate) {
            error.recurrence = 'Please select how many occurrences to create.';
            anyErrors = true;
        }
        if (recurrenceInstances.length > RECURRENCE_MAX_INSTANCES) {
            error.recurrence = `Recurring events are limited to ${RECURRENCE_MAX_INSTANCES} instances.`;
            anyErrors = true;
        }
        if (recurrenceInstances.length < 1) {
            error.recurrence = 'At least one recurring event instance is required.';
            anyErrors = true;
        }

        const { errors: recurrenceErrors } = validateRecurringScheduleInstances(
            recurrenceInstances,
            RECURRENCE_MAX_INSTANCES,
        );

        if (recurrenceErrors?.length) {
            error.recurrence = recurrenceErrors.join(' ');
            anyErrors = true;
        }
    }

    return anyErrors ? error : null;
}

/**
 * Formats a recurrence instance's dates as localised display strings.
 *
 * @param {Object} instance             - Recurrence instance with UTC date fields
 * @param {string} selectedTimeZoneValue - IANA timezone string
 * @returns {{ eventDate: string, regDate: string }}
 */
export function formatInstanceSchedule(instance, selectedTimeZoneValue) {
    if (!instance || !selectedTimeZoneValue) {
        return { eventDate: '', regDate: '' };
    }

    const start = instance.eventStartUtc
        ? moment.utc(instance.eventStartUtc).tz(selectedTimeZoneValue)
        : null;
    const end = instance.eventEndUtc
        ? moment.utc(instance.eventEndUtc).tz(selectedTimeZoneValue)
        : null;
    const regStart = instance.registrationStartUtc
        ? moment.utc(instance.registrationStartUtc).tz(selectedTimeZoneValue)
        : null;
    const regEnd = instance.registrationEndUtc
        ? moment.utc(instance.registrationEndUtc).tz(selectedTimeZoneValue)
        : null;

    const formatRange = (a, b, format) => {
        if (!a || !b || !a.isValid() || !b.isValid()) { return ''; }
        return `${a.format(format)} - ${b.format(format)}`;
    };

    return {
        eventDate: formatRange(start, end, 'ddd, MMM D (h:mma)'),
        regDate: `Reg: ${formatRange(regStart, regEnd, 'MMM D (h:mma)')}`,
    };
}
